import { useCallback, useRef } from "react";
import useApiRequest from "../../../../../utils/hooks/useApiRequest";
import { ADD_TO_BASKET_URL, ADD_TICKET_LIST_URL } from "../../../../api/apiRoutes";
import { BasketItem } from "../../../../models/BasketItem";
import { ApiResult } from "../../../../models/ApiResult";

const CACHE_KEY = "ADD_TICKET_LIST_ENDPOINT_AVAILABLE";

interface AddTicketListPayload {
  tickets: BasketItem[];
  facilityIds: string;
}

interface UseBatchAddToBasket {
  addToBasket: (items: BasketItem | BasketItem[]) => Promise<ApiResult>;
  isNewEndpointAvailable: () => boolean | null;
  resetEndpointCache: () => void;
}

/**
 * Hook do inteligentnego dodawania biletów do koszyka.
 * 
 * Automatycznie wykrywa czy backend obsługuje nowy batch endpoint (/addTicketList)
 * i używa go dla wielu biletów. W przypadku braku wsparcia lub błędu,
 * automatycznie wykonuje fallback na stare API (/addTicket).
 * 
 * Cechy:
 * - Pojedynczy bilet → zawsze stare API (optymalizacja)
 * - Wiele biletów → próba nowego API, fallback na stare
 * - Cache w sessionStorage (unika wielokrotnych prób)
 */
export default function useBatchAddToBasket(): UseBatchAddToBasket {
  const { post } = useApiRequest();
  const isCheckingEndpoint = useRef(false);
  const endpointUnavailableDetected = useRef(false);

  const isNewEndpointAvailable = useCallback((): boolean | null => {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached === null) return null;
    return cached === "true";
  }, []);

  const setCacheEndpointAvailable = useCallback((available: boolean) => {
    sessionStorage.setItem(CACHE_KEY, available.toString());
  }, []);

  const resetEndpointCache = useCallback(() => {
    sessionStorage.removeItem(CACHE_KEY);
  }, []);

  const isEndpointNotSupportedError = useCallback((result: ApiResult): boolean => {
    if (!result.isError) return false;

    // Sprawdź typowe komunikaty błędów dla nieobsługiwanego endpointu
    const errorMessages = result.errorTable || [];
    const unsupportedPatterns = [/not found/i, /404/i, /405/i, /method not allowed/i, /not implemented/i, /501/i];

    return errorMessages.some((msg: string) => unsupportedPatterns.some((pattern) => pattern.test(msg.trim())));
  }, []);

  /**
   * Dodaje bilety używając starego API (fallback).
   * Wysyła requesty SEKWENCYJNIE z 50ms opóźnieniem między nimi,
   * żeby nie przytłoczyć backendu wieloma równoczesnymi requestami.
   */
  const addToBasketLegacy = useCallback(
    async (items: BasketItem[]): Promise<ApiResult> => {
      const results: ApiResult[] = [];

      // Wysyłaj requesty SEKWENCYJNIE z opóźnieniem (nie równolegle!)
      for (let i = 0; i < items.length; i++) {
        const item = items[i];

        try {
          const result = await post(ADD_TO_BASKET_URL, item);
          results.push(result);
        } catch (error) {
          results.push({
            isError: true,
            errorTable: [error instanceof Error ? error.message : "Nieznany błąd"],
            result: null,
            messageType: "error",
          } as ApiResult);
        }

        // Dodaj 50ms opóźnienie między requestami (oprócz ostatniego)
        // To chroni backend przed przeciążeniem
        if (i < items.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
      }

      // Agregacja wyników - zwróć pierwszy błąd lub sukces
      const firstError = results.find((r: ApiResult) => r.isError);
      if (firstError) {
        return firstError;
      }

      // Wszystkie się udały
      return {
        isError: false,
        errorTable: [],
        result: results.map((r: ApiResult) => r.result),
        messageType: "info",
      } as ApiResult;
    },
    [post],
  );

  const addToBasketBatch = useCallback(
    async (items: BasketItem[]): Promise<ApiResult> => {
      const facilityIds = items[0]?.facilityIds || "0";

      const payload: AddTicketListPayload = {
        tickets: items,
        facilityIds: facilityIds,
      };

      try {
        const result = await post(ADD_TICKET_LIST_URL, payload);

        // Sprawdź czy to błąd 404/405 (endpoint nie istnieje)
        if (isEndpointNotSupportedError(result)) {
          // Zaznacz że endpoint nie działa
          endpointUnavailableDetected.current = true;
          // NIE zwracaj błędu 404 - wykonaj fallback
          return await addToBasketLegacy(items);
        }

        return result;
      } catch (error) {
        // Błąd sieciowy lub inny - zwróć go normalnie
        return {
          isError: true,
          errorTable: [error instanceof Error ? error.message : "Nieznany błąd"],
          result: null,
          messageType: "error",
        } as ApiResult;
      }
    },
    [post, isEndpointNotSupportedError, addToBasketLegacy],
  );

  const addToBasket = useCallback(
    async (items: BasketItem | BasketItem[]): Promise<ApiResult> => {
      // Normalizuj do tablicy
      const itemsArray = Array.isArray(items) ? items : [items];

      // Walidacja
      if (itemsArray.length === 0) {
        return {
          isError: true,
          errorTable: ["Brak elementów do dodania"],
          result: null,
          messageType: "error",
        } as ApiResult;
      }

      // Dla pojedynczego elementu zawsze używaj starego API (optymalizacja)
      if (itemsArray.length === 1) {
        return await post(ADD_TO_BASKET_URL, itemsArray[0]);
      }

      // Dla wielu elementów sprawdź dostępność nowego API
      const cachedAvailability = isNewEndpointAvailable();

      // Jeśli cache mówi że nieobsługiwany - użyj legacy
      if (cachedAvailability === false) {
        return await addToBasketLegacy(itemsArray);
      }

      // Jeśli cache mówi że obsługiwany - użyj nowego
      if (cachedAvailability === true) {
        endpointUnavailableDetected.current = false;
        // addToBasketBatch już obsługuje fallback wewnętrznie, nigdy nie zwróci 404
        const result = await addToBasketBatch(itemsArray);

        // Double-check: jeśli wykryto że endpoint przestał działać
        if (endpointUnavailableDetected.current) {
          setCacheEndpointAvailable(false);
        }

        return result;
      }

      // Brak cache - próbuj nowego API i zapisz wynik
      if (!isCheckingEndpoint.current) {
        isCheckingEndpoint.current = true;
        endpointUnavailableDetected.current = false;

        // addToBasketBatch już obsługuje fallback wewnętrznie i ustawi endpointUnavailableDetected
        const result = await addToBasketBatch(itemsArray);

        // Sprawdź czy wykryto że endpoint nie istnieje
        if (endpointUnavailableDetected.current) {
          setCacheEndpointAvailable(false);
        } else if (!result.isError) {
          // Sukces i endpoint nie był niedostępny - endpoint działa!
          setCacheEndpointAvailable(true);
        }
        // Jeśli błąd ale nie 404, cache pozostaje null (nie wiemy czy endpoint istnieje czy inny problem)

        isCheckingEndpoint.current = false;
        return result;
      }

      // Jeśli już trwa sprawdzanie, użyj legacy jako fallback
      return await addToBasketLegacy(itemsArray);
    },
    [post, isNewEndpointAvailable, addToBasketLegacy, addToBasketBatch, setCacheEndpointAvailable],
  );

  return {
    addToBasket,
    isNewEndpointAvailable,
    resetEndpointCache,
  };
}
